import { ReactNode } from 'react';

import {
  getThemeColor,
  IconButton,
  IconButtonProps,
  Icons,
  mergeSx,
  Paper,
  SxProps,
  theme,
  Typography,
  Box,
  Stack,
} from '@cast/design-system';

import { mainLayoutCssVars } from 'main-layout-op/cssVars';
import type { TypographyProps } from '@cast/design-system';

/**
 * @deprecated Use `MarketingBanner` from `@cast/design-system` instead.
 * In app pages, wrap it with `PageMarketingBanner` from `components/portals`
 * (which portals into the `PageHeader` outlet and wraps children in `MarketingBannersStack`).
 * See `libs/design-system/src/components-v2/info-display/MarketingBanner/MarketingBanner.mdx`
 * for usage guidance.
 */
type CloseableBannerProps = {
  icon?: ReactNode;
  title: ReactNode;
  body: ReactNode;
  actionButton?: ReactNode;
  constrainWidthOnOp?: boolean;
  onDismiss?: () => void;
  onClose?: () => void;
  testId?: string;
  sx?: SxProps;
  titleProps?: TypographyProps;
  bodyProps?: TypographyProps;
  closeButtonProps?: IconButtonProps;
  closeButtonColor?: string;
  actionPosition?: 'bottom' | 'right';
  hideCloseButton?: boolean;
};

/**
 * @deprecated Use `MarketingBanner` from `@cast/design-system` instead.
 * In app pages, wrap it with `PageMarketingBanner` from `components/portals`.
 */
export const CloseableBanner = ({
  icon,
  title,
  body,
  actionButton,
  testId,
  onDismiss,
  onClose,
  constrainWidthOnOp = false,
  actionPosition = 'bottom',
  sx: sxProp,
  titleProps,
  bodyProps,
  closeButtonColor = 'common.white',
  hideCloseButton = false,
  closeButtonProps,
}: CloseableBannerProps) => {
  const horizontalPadding = mainLayoutCssVars.getters.horizontalPadding();

  return (
    <Paper
      sx={mergeSx(
        {
          display: 'flex',
          bgcolor: 'grey.800',
          gap: 10,
          padding: 6,
          width: '100%',
          position: 'absolute',
          top: -20,
          left: 0,
          zIndex: 'drawer',
          border: 'aqua.400',
          boxShadow: `0px 0px 4px 0px ${getThemeColor(theme, 'aqua.400')}`,
        },
        constrainWidthOnOp
          ? {
              left: horizontalPadding,
              width: `calc(100% - (${horizontalPadding} * 2))`,
            }
          : undefined,
        sxProp
      )}
      testId={testId}
    >
      {icon}
      <Stack>
        {typeof title === 'string' ? (
          <Typography
            variant="h5"
            color="white"
            lineHeight={33}
            {...titleProps}
          >
            {title}
          </Typography>
        ) : (
          title
        )}
        <Typography variant="P12R" color="grey.200" {...bodyProps}>
          {body}
        </Typography>
        {actionPosition === 'bottom' && actionButton}
      </Stack>
      {actionPosition === 'right' && actionButton && (
        <Box alignSelf="center" ml="auto" pr={26}>
          {actionButton}
        </Box>
      )}

      {!hideCloseButton && (
        <IconButton
          size="lg"
          onClick={() => {
            onDismiss?.();
            onClose?.();
          }}
          testId="close-btn"
          {...closeButtonProps}
          sx={mergeSx(
            {
              position: 'absolute',
              top: 0,
              right: 0,
              opacity: 0.4,
              transition: 'opacity .25s',
              '&:hover': { opacity: 0.75 },
            },
            closeButtonProps?.sx
          )}
        >
          <Icons.X color={getThemeColor(theme, closeButtonColor)} />
        </IconButton>
      )}
    </Paper>
  );
};
